import { useQuery, useQueryClient } from "@tanstack/react-query";
import { ButtonSecondary } from "components/Atoms/Button";
import { Loading } from "components/panels";
import moment from "moment";
import { PlanningContext } from "PlanningContext/PlanningContext";
import React, { useContext } from "react";
import Panel from "../../components/panels/panel";
import AppointmentTypeFilter from "./AppointmentTypeFilter";
import Itinerary from "./Itinerary";
import PhaseFilter from "./PhaseFilter";
import { ItinerarySidebarItem, LinkedItineraryItems } from "./utils";
import { invalidateFlightsCache } from "utilities/api/flightQuery";
import { invalidateTrainsCache } from "utilities/api/trainQuery";
import { invalidateGroundTransportsCache } from "utilities/api/groundTransportQuery";
import { invalidateShowsCache } from "utilities/api/showQuery";

export interface ItinerarySidebarBaseProps {
  item: ItinerarySidebarItem
  artist: server.dto.ArtistSmall
  startDate: moment.Moment
  endDate: moment.Moment

  disableNewAppointments: boolean
  canLinkUnlinkTravel: boolean
  canLinkUnlinkShow: boolean
  canAdd: boolean

  queryKeys: string

  linkedItems: LinkedItineraryItems
}

const itineraryTypesKey = "itinerary_types";
const itineraryPhasesKey = "itinerary_phases";

const getFromLocalStorage = (key: string): string[] => {
  const values = localStorage.getItem(key);
  if (values) {
    return values.split(",");
  }
  return undefined;
};

const setLocalStorage = (key: string, values: string[]) => {
  localStorage.setItem(key, values.join(","));
};

const defaultAppointmentTypes = serverReference.appointmentTypes.map(a => a.value);
const defaultPhases = serverReference.appointmentPhases.map(a => a.value);

export const itinerarySidebarQueryKey = "itinerarySidebar";
export const itineraryShowsSidebarQueryKey = "linkedTravelToShowsItinerary";

interface LinkedToShowIds {
  flightIds: number[],
  groundTransportIds: number[],
  trainIds: number[],
}

const ItinerarySidebarBase = (props: ItinerarySidebarBaseProps) => {
  const planningContext = useContext(PlanningContext);

  const [startDate, setStartDate] = React.useState(moment.utc(props.startDate).subtract(2, "days"));
  const [endDate, setEndDate] = React.useState(moment.utc(props.endDate).add(3, "days"));
  const [phases, setPhases] = React.useState<server.dto.AppointmentPhases[]>(getFromLocalStorage(itineraryPhasesKey) as server.dto.AppointmentPhases[] ?? defaultPhases);
  const [appointmentTypes, setAppointmentTypes] = React.useState<server.dto.AppointmentTypes[]>(getFromLocalStorage(itineraryTypesKey) as server.dto.AppointmentTypes[] ?? defaultAppointmentTypes);

  const [linkedShowIds, setLinkedShowIds] = React.useState<number[]>(props.linkedItems.showIds);

  const queryClient = useQueryClient();

  const instruction: server.dto.PlanningForItineraryQuery = {
    start: startDate.toISOString() as any,
    end: endDate.toISOString() as any,
    phases,
    appointmentTypes: appointmentTypes,
    artistIds: [props.artist.artistId],
  };

  const daysBetweenStartEnd = endDate.diff(startDate, "days");

  const api = useQuery({
    queryKey: [itinerarySidebarQueryKey, props.queryKeys, instruction],
    queryFn: async () => {
      return window.CoreApi.Planning.PlanningForItinerary(instruction);
    },
    keepPreviousData: true,
    enabled: daysBetweenStartEnd < serverReference.constants.appointment.maxDurationDays
  });
  const items = api.data?.data.data ?? [];

  const linkedTravelToShowsQuery = useQuery({
    queryKey: [itineraryShowsSidebarQueryKey, props.queryKeys, props.item.itemId],
    queryFn: async (): Promise<LinkedToShowIds> => {
      const resp = await window.CoreApi.Show.GetLinkedTravel(props.item.itemId);
      if (resp.data.hasErrors) {
        return {
          flightIds: [],
          groundTransportIds: [],
          trainIds: [],
        };
      }
      return {
        flightIds: resp.data.data.flights.map(f => f.flightId),
        groundTransportIds: resp.data.data.groundTransport.map(g => g.groundTransportId),
        trainIds: resp.data.data.trains.map(t => t.trainId),
      }
    },
    initialData: () => ({
      flightIds: props.linkedItems.flightIds,
      groundTransportIds: props.linkedItems.groundTransportIds,
      trainIds: props.linkedItems.trainIds,
    }),
    initialDataUpdatedAt: Date.now(),
    staleTime: Infinity,
  });

  const loadPreviousAppointments = () => {
    const newStart = startDate.clone().subtract(2, "days");
    setStartDate(newStart);
  };

  const loadNextAppointments = () => {
    const newEnd = endDate.clone().add(3, "days");
    setEndDate(newEnd);
  };

  const onAppointmentToggled = (type: server.dto.AppointmentTypes) => {
    const doesTypeAlreadyExist = appointmentTypes.includes(type);
    let newTypes: server.dto.AppointmentTypes[];
    if (doesTypeAlreadyExist) {
      newTypes = appointmentTypes.filter(t => t !== type);
    } else {
      newTypes = [...appointmentTypes, type];
    }

    if (newTypes.length === 0) {
      newTypes = serverReference.appointmentTypes.map(a => a.value).filter(a => a !== "unknown");
    }
    setAppointmentTypes(newTypes);
    setLocalStorage(itineraryTypesKey, newTypes);
  };

  const onPhaseToggled = (phase: server.dto.AppointmentPhases) => {
    const doesPhaseAlreadyExist = phases.includes(phase);
    let newPhases: server.dto.AppointmentPhases[];
    if (doesPhaseAlreadyExist) {
      newPhases = phases.filter(p => p !== phase);
    } else {
      newPhases = [...phases, phase];
    }
    if (newPhases.length === 0) {
      newPhases = serverReference.appointmentPhases.map(a => a.value).filter(a => a !== "unknown");
    }
    setPhases(newPhases);
    setLocalStorage(itineraryPhasesKey, newPhases);
  };

  const toggleLinkedFlight = async (appointment: server.dto.PlannerAppointment) => {
    if (props.item.type !== "show") return;

    const isAlreadyLinked = linkedTravelToShowsQuery.data.flightIds.includes(appointment.flightId);

    if (isAlreadyLinked) {
      await window.CoreApi.Show.UnlinkFlight({
        flightId: appointment.flightId,
        showId: props.item.itemId,
      });
    } else {
      await window.CoreApi.Show.LinkFlight({
        flightId: appointment.flightId,
        showId: props.item.itemId,
      });
    }

    invalidateFlightsCache(queryClient);
  }

  const toggleLinkedTrain = async (appointment: server.dto.PlannerAppointment) => {
    if (props.item.type !== "show") return;

    const isAlreadyLinked = linkedTravelToShowsQuery.data.trainIds.includes(appointment.trainId);

    if (isAlreadyLinked) {
      await window.CoreApi.Show.UnlinkTrainSegment({
        trainId: appointment.trainId,
        showId: props.item.itemId,
      });
    } else {
      await window.CoreApi.Show.LinkTrain({
        trainId: appointment.trainId,
        showId: props.item.itemId,
      });
    }

    invalidateTrainsCache(queryClient);
  }

  const toggleLinkedGroundTransport = async (appointment: server.dto.PlannerAppointment) => {
    if (props.item.type !== "show") return;

    const isAlreadyLinked = linkedTravelToShowsQuery.data.groundTransportIds.includes(appointment.groundTransportId);

    if (isAlreadyLinked) {
      await window.CoreApi.Show.UnlinkGroundTransport({
        groundTransportId: appointment.groundTransportId,
        showId: props.item.itemId,
      });
    } else {
      await window.CoreApi.Show.LinkGroundTransport({
        groundTransportId: appointment.groundTransportId,
        showId: props.item.itemId,
      });
    }

    invalidateGroundTransportsCache(queryClient);
  }

  const toggleLinkedShow = async (appointment: server.dto.PlannerAppointment) => {
    if (props.item.type === "show") return;

    const isAlreadyLinked = linkedShowIds.includes(appointment.showId);

    if (isAlreadyLinked) {
      setLinkedShowIds(oldIds => oldIds.filter(oldId => oldId !== appointment.showId));
      if (props.item.type === "groundTransport") {
        await window.CoreApi.Show.UnlinkGroundTransport({
          groundTransportId: props.item.itemId,
          showId: appointment.showId,
        });
      } else if (props.item.type === "flight") {
        await window.CoreApi.Show.UnlinkFlight({
          flightId: props.item.itemId,
          showId: appointment.showId,
        });
      } else if (props.item.type === "train") {
        await window.CoreApi.Show.UnlinkTrainSegment({
          trainId: props.item.itemId,
          showId: appointment.showId,
        });
      }
    } else {
      setLinkedShowIds(oldIds => [...oldIds, appointment.showId]);
      if (props.item.type === "groundTransport") {
        await window.CoreApi.Show.LinkGroundTransport({
          groundTransportId: props.item.itemId,
          showId: appointment.showId,
        });
      } else if (props.item.type === "flight") {
        await window.CoreApi.Show.LinkFlight({
          flightId: props.item.itemId,
          showId: appointment.showId,
        });
      } else if (props.item.type === "train") {
        await window.CoreApi.Show.LinkTrain({
          trainId: props.item.itemId,
          showId: appointment.showId,
        });
      }
    }

    invalidateShowsCache(queryClient);
  }

  const toggleLinked = async (appointment: server.dto.PlannerAppointment) => {
    switch (appointment.type.value) {
      case "flight":
        await toggleLinkedFlight(appointment);
        return;
      case "train":
        await toggleLinkedTrain(appointment);
        return;
      case "groundTransport":
        await toggleLinkedGroundTransport(appointment);
        return;
      case "show":
        await toggleLinkedShow(appointment);
        return;
    }
  };

  React.useEffect(() => {
    setStartDate(moment.utc(props.startDate).subtract(2, "days"));
    setEndDate(moment.utc(props.endDate).add(3, "days"));
  }, [props.endDate, props.startDate]);

  const typeFilterMap = new Set(appointmentTypes);
  const phaseFilterMap = new Set(phases);

  const onNewButtonPressed = () => {
    planningContext.open({
      artist: props.artist,
      date: props.startDate,
      fromShowId: props.item.type === "show" ? props.item.itemId : undefined,
    });
  };

  const canAddNewAppointments = props.canAdd && !props.disableNewAppointments;

  return <Panel
    panelClassName="s1_related-feature-panel"
    title={props.artist.name}
    contentCssClass="flex-column gap-larger"
    button={canAddNewAppointments && <ButtonSecondary onClick={onNewButtonPressed} label={RESX.Buttons.btnNew} />}
  >

    <div style={{ display: "table", width: "100%" }} className="margin-top-larger">
      <div style={{ display: "table-cell" }}>
        <AppointmentTypeFilter type="show" isToggled={typeFilterMap.has("show")} onToggled={onAppointmentToggled} />
        <AppointmentTypeFilter type="flight" isToggled={typeFilterMap.has("flight")} onToggled={onAppointmentToggled} />
        <AppointmentTypeFilter type="train" isToggled={typeFilterMap.has("train")} onToggled={onAppointmentToggled} />
        <AppointmentTypeFilter type="groundTransport" isToggled={typeFilterMap.has("groundTransport")} onToggled={onAppointmentToggled} />
        <AppointmentTypeFilter type="accommodation" isToggled={typeFilterMap.has("accommodation")} onToggled={onAppointmentToggled} />
        <AppointmentTypeFilter type="externalCalendar" isToggled={typeFilterMap.has("externalCalendar")} onToggled={onAppointmentToggled} />
      </div>
      <div style={{ display: "table-cell", textAlign: "right" }}>
        <PhaseFilter phase={"option"} isToggled={phaseFilterMap.has("option")} onToggled={onPhaseToggled} />
        <PhaseFilter phase={"confirmed"} isToggled={phaseFilterMap.has("confirmed")} onToggled={onPhaseToggled} />
        <PhaseFilter phase={"cancelled"} isToggled={phaseFilterMap.has("cancelled")} onToggled={onPhaseToggled} />
      </div>
    </div>
    <div>
      <div className="s1_morebutton s1_previous" onClick={loadPreviousAppointments}>
        <i className="icon-angle-up"></i>
      </div>
      {items.length > 0 && <Itinerary
        artist={props.artist}
        appointments={items}
        startMoment={startDate}
        endMoment={endDate}
        item={props.item}
        linkedItems={{
          flightIds: linkedTravelToShowsQuery.data.flightIds,
          groundTransportIds: linkedTravelToShowsQuery.data.groundTransportIds,
          trainIds: linkedTravelToShowsQuery.data.trainIds,
          showIds: linkedShowIds,
        }}
        toggleLinked={toggleLinked}
        canCreateNewAppointment={canAddNewAppointments}
        canLinkUnlinkTravel={props.canLinkUnlinkTravel}
        canLinkUnlinkShow={props.canLinkUnlinkShow}
      />}
      {api.isInitialLoading && <Loading height={50} />}
      <div className="s1_morebutton s1_next" onClick={loadNextAppointments}>
        <i className="icon-angle-down"></i>
      </div>
    </div>
  </Panel>;
};

export default ItinerarySidebarBase;